<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import {
    AiChatQuestionTemplatesService,
    type AiChatQuestionTemplate,
    type CustomerUser as User
  } from '@/types/generated'
  import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { Button } from '@/components/ui/button'
  import { Search } from 'lucide-vue-next'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import { ROUTES } from '@/router/routes'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { genericErrorToast } from '@/lib/toasts'
  import { useUserStore } from '@/shared/stores/userStore'

  const router = useRouter()
  const templates = ref<AiChatQuestionTemplate[]>([])
  const searchQuery = ref('')
  const selectedLawField = ref('')
  const showDeleteModal = ref(false)
  const templateToDelete = ref<AiChatQuestionTemplate | null>(null)
  const { fetchUser } = useUserStore()
  const user = computed<User>(() => useUserStore().user)
  const lawFieldOptions = computed<User>(() => useUserStore().lawFieldOptions)

  onMounted(async () => {
    const fetching = [fetchTemplates()]
    if (!user.value) {
      fetching.push(fetchUser())
    }
    await Promise.all(fetching)
  })

  const fetchTemplates = async () => {
    templates.value = (
      await AiChatQuestionTemplatesService.aiChatQuestionTemplatesList({ limit: 0 })
    ).results
  }

  const filteredTemplates = computed(() => {
    if (searchQuery.value === '' && selectedLawField.value === '') {
      return templates.value
    }
    return templates.value.filter(
      (template) =>
        template.title.toLowerCase().includes(searchQuery.value.toLowerCase()) &&
        (selectedLawField.value === '' || template.law_field === selectedLawField.value)
    )
  })

  const openDeleteModal = (template: AiChatQuestionTemplate) => {
    templateToDelete.value = template
    showDeleteModal.value = true
  }

  const closeDeleteModal = () => {
    showDeleteModal.value = false
    templateToDelete.value = null
  }

  const confirmDelete = async () => {
    if (templateToDelete.value) {
      try {
        await AiChatQuestionTemplatesService.aiChatQuestionTemplatesDestroy({
          uuid: templateToDelete.value.uuid
        })
        closeDeleteModal()
        await fetchTemplates()
      } catch (error) {
        genericErrorToast()
      }
    }
  }

  const createTemplate = async () => {
    try {
      const newTemplate = await AiChatQuestionTemplatesService.aiChatQuestionTemplatesCreate({
        requestBody: {
          title: 'Neue Vorlage',
          law_field: '',
          prompt: '(leer)'
        }
      })
      await router.push(
        ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATE_DETAIL.replace(':uuid', newTemplate.uuid)
      )
    } catch (error) {
      console.error('Failed to create new template:', error)
      genericErrorToast()
    }
  }
</script>

<template>
  <SettingsPage v-if="templates && user">
    <template #actions>
      <button
        class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
          text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
          focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
        @click="createTemplate"
      >
        <svg
          class="mr-2 h-4 w-4"
          fill="none"
          height="24"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <line
            x1="12"
            x2="12"
            y1="5"
            y2="19"
          />
          <line
            x1="5"
            x2="19"
            y1="12"
            y2="12"
          />
        </svg>
        Vorlage erstellen
      </button>
    </template>
    <div class="space-y-4">
      <div class="flex items-center space-x-4">
        <div class="relative flex items-center">
          <Search class="ml-3 mr-2 h-4 w-4 text-gray-400" />
          <InputField
            v-model="searchQuery"
            name="search"
            placeholder="Suchen"
          />
        </div>
        <div class="w-56 flex-shrink-0">
          <SelectField
            v-model="selectedLawField"
            :options="lawFieldOptions"
            name="rechtsgebiet"
            title="Rechtsgebiet"
            :select-option-text="'Rechtsgebiet'"
            :allow-unselect="true"
            unselected-option-text="Alle Rechtsgebiete"
          />
        </div>
      </div>

      <Table v-if="templates.length > 0">
        <TableHeader>
          <TableRow>
            <TableCell class="w-1/3 py-2 font-medium">Name</TableCell>
            <TableCell class="w-1/6 py-2 font-medium">Typ</TableCell>
            <TableCell class="w-1/6 py-2 font-medium">Rechtsgebiet</TableCell>
            <TableCell class="w-1/6 py-2 font-medium">Rechtsthema</TableCell>
            <TableCell class="w-1/6 py-2 font-medium">Aktualisiert</TableCell>
            <TableCell class="w-12 py-2"></TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow
            v-for="template in filteredTemplates"
            :key="template.uuid"
            class="cursor-pointer hover:bg-gray-50"
            @click="
              router.push(
                ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATE_DETAIL.replace(':uuid', template.uuid)
              )
            "
          >
            <TableCell class="py-4">{{ template.title }}</TableCell>
            <TableCell class="py-1">
              <span
                v-if="template.is_jupus_template"
                class="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700
                  ring-1 ring-inset ring-purple-700/10"
              >
                Jupus
              </span>
              <span
                v-else
                class="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1
                  ring-inset ring-red-700/10"
              >
                Kanzlei
              </span>
            </TableCell>
            <TableCell class="py-1">{{ template.law_field || '-' }}</TableCell>
            <TableCell class="py-1">{{ template.law_topic || '-' }}</TableCell>
            <TableCell class="py-1">
              {{ new Date(template.updated_at).toLocaleDateString('de-DE') }}
            </TableCell>
            <TableCell class="py-1">
              <div @click.stop>
                <TableOptionsButton
                  :options="[
                    {
                      label: 'Bearbeiten',
                      to: ROUTES.APP.SETTINGS.AI.PROMPT_TEMPLATE_DETAIL.replace(
                        ':uuid',
                        template.uuid
                      )
                    },
                    {
                      label: 'Löschen',
                      onClick: () => openDeleteModal(template)
                    }
                  ]"
                />
              </div>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <div
        v-else
        class="py-4 text-center"
      >
        Keine Vorlagen gefunden.
      </div>
    </div>
  </SettingsPage>

  <!-- Delete Confirmation Modal -->
  <Dialog
    :open="showDeleteModal"
    @update:open="closeDeleteModal"
  >
    <DialogContent v-if="templateToDelete">
      <DialogHeader>
        <template v-if="templateToDelete?.is_jupus_template">
          <DialogTitle>Löschen nicht möglich</DialogTitle>
          <DialogDescription>JUPUS Vorlagen können nicht gelöscht werden.</DialogDescription>
        </template>
        <template v-else>
          <DialogTitle>Löschen bestätigen</DialogTitle>
          <DialogDescription>
            Sind Sie sicher, dass Sie die Vorlage "{{ templateToDelete?.title }}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogDescription>
        </template>
      </DialogHeader>
      <DialogFooter>
        <Button
          variant="secondary"
          @click="closeDeleteModal"
        >
          Abbrechen
        </Button>
        <Button
          v-if="!templateToDelete?.is_jupus_template"
          variant="destructive"
          @click="confirmDelete"
        >
          Löschen
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
